import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Card, CardContent } from "../../ui/card";
import {
    Monitor,
    Apple,
    ShieldCheck,
    Cpu,
    ChevronLeft,
    ChevronRight,
    Square,
    Terminal,
    Laptop,
    Smartphone,
    Loader2,
    Clock,
    UserCheck,
    Check
} from "lucide-react";
import { useLocation } from "wouter";
import { Device, DeviceLoadingState } from "../interfaces/EndpointInterfaces";
import StatusIndicator from "../Endpoints/StatusIndicator";
import RequestConnectAccessDialog from "./RequestConnectAccessDialog";
import { Skeleton } from "../../ui/skeleton"; // ✅ Correct import
import { getAllWindowsCredentials, getUsers } from "./Api/tenantuserendpointsapi";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import { FaLinux, FaWindows } from "react-icons/fa";
import EmptyStateCard from "../EmptyStateCard";
import { FaApple } from "react-icons/fa6";
import useNotificationStore from "../../../services/notificationStore";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { NGROK } from "../../../APIs";
import {
    LIST_STATE_KEYS,
    writePersistedListState,
} from "../../../utils/listPaginationState";

interface TenantDeviceTableProps {
    devices: Device[];
    onConnect?: (device: Device, mode: string, label: string) => void;
    isLoading: boolean | false;
    totalDevicesCount: number;
    deviceLoadingState: Record<string, DeviceLoadingState>;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;

}

const DEVICES_PER_PAGE = 15;

export default function TenantDeviceTable({
    devices,
    onConnect,
    isLoading,
    totalDevicesCount,
    deviceLoadingState,
    page,
    setPage
}: TenantDeviceTableProps) {
    const navigate = useNavigate();
    const [showAccessDialog, setShowAccessDialog] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [usersList, setUsersList] = useState<any[]>([]);
    const [pendingRequestDeviceIds, setPendingRequestDeviceIds] = useState<Record<string, boolean>>({});
    let tenant = myLocalStorage.getItem("latestTenant");
    let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;
    let { email, role, userRole } = useUserStore((state) => state.user);
    const { userApprovedNotifications, setUserApprovedNotifications } =
        useNotificationStore();
    const isFetchingSummaryRef = useRef(false);
    const [remainingTimes, setRemainingTimes] = useState<Record<string, string>>({});
    const [remainingSeconds, setRemainingSeconds] = useState(30 * 60);

    const isOwnDevices = (device: Device): boolean => {
        return device.belongedToUser === email;
    }

    const getApprovedAccessNotification = (deviceId: string) => {
        return userApprovedNotifications.find(
            (item: any) =>
                Number(item.computerId) === Number(deviceId) &&
                item.requestStatus === "APPROVED" &&
                item.resourceType === "GROUP" &&
                item.resourceName === "Whiteswan Access"
        );
    };

    const hasPendingRequest = (deviceId: string) => pendingRequestDeviceIds[deviceId] === true;

    const shouldShowConnectButton = (device: Device): boolean => {
        const item = getApprovedAccessNotification(device.id);
        if (!item) {
            return false;
        }

        const nowUTC = moment.utc();

        // Scheduled access: timer/connect driven by schedule window only.
        if (item.schedules && item.schedules.length > 0) {
            return item.schedules.some((schedule: any) =>
                nowUTC.isBetween(
                    moment.utc(schedule.scheduledStartTime),
                    moment.utc(schedule.scheduledEndTime),
                    null,
                    "[]",
                ),
            );
        }

        if (item.accessTimeOverAt && nowUTC.isAfter(moment.utc(item.accessTimeOverAt))) {
            return false;
        }

        return true;
    };

    const getAccessEndMoment = (item: any): moment.Moment | null => {
        if (!item) return null;

        if (item.schedules && item.schedules.length > 0) {
            const nowUTC = moment.utc();
            const activeSchedule = item.schedules.find((schedule: any) =>
                nowUTC.isBetween(
                    moment.utc(schedule.scheduledStartTime),
                    moment.utc(schedule.scheduledEndTime),
                    null,
                    "[]",
                ),
            );
            if (!activeSchedule) return null;
            return moment.utc(activeSchedule.scheduledEndTime);
        }

        if (item.accessTimeOverAt) {
            const raw = item.accessTimeOverAt;
            return moment.utc(raw.endsWith("Z") ? raw : `${raw}Z`);
        }

        return null;
    };

    const refreshApprovedAccessNotifications = useCallback(async () => {
        if (!email || !tenantName || isFetchingSummaryRef.current) return;
        isFetchingSummaryRef.current = true;

        const controller = new AbortController();

        try {
            const params: Record<string, string> = {
                role,
                tenantName,
            };

            if (!email.startsWith("wsadmin")) {
                params.email = email;
            }

            const response = await axios.get(`${NGROK}/api/access/summary`, {
                params,
                signal: controller.signal,
            });

            if (response?.data?.onPremAccess) {
                setUserApprovedNotifications(response.data.onPremAccess);
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Error fetching summary:", error);
            }
        } finally {
            isFetchingSummaryRef.current = false;
        }
    }, [email, role, tenantName, setUserApprovedNotifications]);

    const handleRequestSubmitSuccess = useCallback(async () => {
        if (!selectedDevice?.id) {
            await refreshApprovedAccessNotifications();
            return;
        }

        setPendingRequestDeviceIds((prev) => ({
            ...prev,
            [selectedDevice.id]: true,
        }));

        await refreshApprovedAccessNotifications();
    }, [refreshApprovedAccessNotifications, selectedDevice]);

    const isDeviceSSh = (device: Device) => {
        return (
            !device?.isManuallyAddedMachine &&
            (
                device?.OperatingSystem?.toLowerCase().includes("linux") ||
                device?.OperatingSystem?.toLowerCase().includes("macos")
            )
        );
    };

    const labelStatus = (mode: string, osmode: string) => {
        let status = mode
        if (mode === "INHERITED_FROM_PROFILE") status = osmode;
        if (mode === "INHERITED_FROM_PROFILE" && osmode === "INHERITED_FROM_TENANT") status = tenant.mode
        return status.toUpperCase();

    }

    const fetchAllUsers = async (device: Device) => {
        if (device?.isManuallyAddedMachine) {
            const response = await getAllWindowsCredentials(tenantName, device.id);
            setUsersList(response || []);
            return;
        }

        if (isDeviceSSh(device)) {

            let response = await getUsers(tenantName, device.id, email);
            if (response) {
                let content: any[] = response;

                if (userRole === "epam-user" ||
                    userRole === "server-pam-user" ||
                    userRole === "developer-user" ||
                    userRole === "thirdparty-user") {
                    content = response.filter((data: any) => data.cn !== "wsadmin");
                }
                setUsersList(content);
            }

        } else {
            let response = await getAllWindowsCredentials(tenantName, device.id);
            setUsersList(response);
        };
    }

    const isDeviceWindows = (device: Device): boolean => {
        if (device.OperatingSystem && device.OperatingSystem.trim().length > 0) {
            return device.OperatingSystem.toLowerCase().includes("windows");
        }
        return false;
    };

    const handleConnect = (deviceId: string) => {
        const device = devices.find((d) => d.id === deviceId);
        setUsersList([])
        if (!device) return;
        const deviceNotification = getApprovedAccessNotification(device.id);
        const canConnectImmediately = device?.hasAccess || shouldShowConnectButton(device);
        console.log("Device Notification:", deviceNotification);

        const mode = deviceNotification?.connectionMode || "";
        const label = deviceNotification?.connectionLabel || "";
        if (canConnectImmediately) {

            if (isDeviceWindows(device) || device?.isManuallyAddedMachine) {
                onConnect?.(device, mode, label);
            } else {
                onConnect?.(device, "", "");
            }

        } else if (device) {
            fetchAllUsers(device);
            setSelectedDevice(device);
            setShowAccessDialog(true);
        }

    };


    const getOSIcon = (osString: string) => {
        const os = osString.toLowerCase();
        const sizeClass = "w-7 h-7";

        if (os.includes("windows"))
            return <FaWindows className={`${sizeClass} text-blue-600 dark:text-blue-400`} />;
        if (os.includes("mac") || os.includes("darwin"))
            return <FaApple className={`${sizeClass} text-gray-800 dark:text-white`} />; // white in dark mode
        if (
            os.includes("ubuntu") ||
            os.includes("centos") ||
            os.includes("rhel") ||
            os.includes("rocky") ||
            os.includes("linux")
        )
            return <FaLinux className={`${sizeClass} text-black dark:text-white`} />; // white in dark mode

        return <FaLinux className={`${sizeClass} text-gray-500 dark:text-gray-400`} />;
    };


    const getOSType = (device: Device): "windows" | "linux" | "manual" => {
        if (device?.isManuallyAddedMachine) return "manual";
        const os = device.os.toLowerCase();
        if (os.includes("windows")) return "windows";
        if (os.includes("linux") || os.includes("ubuntu") || os.includes("mac") || os.includes("darwin"))
            return "linux";
        return "linux";
    };

    const handleDeviceClick = (deviceId: string) => {
        const device = devices.find((d) => d.id === deviceId);
        if (device) {
            if (device?.hasAccess || shouldShowConnectButton(device) || isOwnDevices(device)) {
                writePersistedListState(LIST_STATE_KEYS.endpoints, { page });
                navigate(`/endpoints/tenantdevice/${deviceId}`, { state: { device } });
            }
        }

    };

    function getRemainingTime(device: Device) {
        const deviceNotification = getApprovedAccessNotification(device.id);
        if (!deviceNotification || !shouldShowConnectButton(device)) {
            return null;
        }

        const endMoment = getAccessEndMoment(deviceNotification);
        if (!endMoment || !endMoment.isValid()) {
            return null;
        }

        const timeDiff = endMoment.diff(moment.utc());
        if (timeDiff <= 0) {
            return "Expired";
        }

        const hours = Math.floor(timeDiff / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

        return `${hours}h ${minutes}m ${seconds}s`;
    }


    useEffect(() => {
        const updateAllRemainingTimes = () => {
            setRemainingTimes((prev) => {
                const updated: Record<string, string> = {};

                devices.forEach((device) => {
                    const time = getRemainingTime(device);
                    if (time && time !== "Expired") {
                        updated[device.id] = time;
                    }
                });

                return updated;
            });
        };

        // Run immediately and then every second
        updateAllRemainingTimes();
        const interval = setInterval(updateAllRemainingTimes, 1000);

        return () => clearInterval(interval);
    }, [devices, userApprovedNotifications]);

    useEffect(() => {
        if (!email || !tenantName) return;

        refreshApprovedAccessNotifications();
        const interval = setInterval(refreshApprovedAccessNotifications, 5000);

        return () => {
            clearInterval(interval);
        };
    }, [email, tenantName, refreshApprovedAccessNotifications]);

    useEffect(() => {
        if (Object.keys(pendingRequestDeviceIds).length === 0) {
            return;
        }

        const approvedDeviceIds = new Set(
            userApprovedNotifications
                .filter((item: any) => item.requestStatus === "APPROVED" && item.resourceName === "Whiteswan Access")
                .map((item: any) => String(item.computerId))
        );

        setPendingRequestDeviceIds((prev) => {
            const next = { ...prev };
            let changed = false;

            Object.keys(next).forEach((deviceId) => {
                if (approvedDeviceIds.has(deviceId)) {
                    delete next[deviceId];
                    changed = true;
                }
            });

            return changed ? next : prev;
        });
    }, [userApprovedNotifications, pendingRequestDeviceIds]);
    useEffect(() => {
        const timer = setInterval(() => {
            setRemainingSeconds((prev) => {
                if (prev <= 0) return 0;
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const getOperatingModeColor = (mode: string, osMode: string) => {
        let status = labelStatus(mode, osMode);
        switch (status.toLowerCase()) {
            case "enforcing":
                return "destructive";
            case "auditing":
                return "default";
            case "disabled":
                return "secondary";
            default:
                return "secondary";
        }
    };

    const totalPages = Math.ceil(totalDevicesCount / DEVICES_PER_PAGE);

    const handlePageChange = (page: number) => {
        setPage(page);
        setCurrentPage(page);
    };

    const renderSkeletonCards = () => {
        return Array.from({ length: DEVICES_PER_PAGE }).map((_, index) => (
            <Card
                key={`skeleton-${index}`}
                className="h-full flex flex-col border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
            >
                <CardContent className="p-6 space-y-4 flex flex-col flex-1">
                    <div className="flex items-center gap-4">
                        <Skeleton className="w-12 h-12 rounded-xl" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="w-3/4 h-5" />
                            <Skeleton className="w-1/2 h-4" />
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <Skeleton className="w-24 h-4" />
                            <Skeleton className="w-16 h-4" />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Skeleton className="w-16 h-5 rounded-full" />
                        <Skeleton className="w-20 h-5 rounded-full" />
                    </div>
                    <Skeleton className="w-full h-9 mt-auto rounded-md" />
                </CardContent>
            </Card>
        ));
    };


    return (
        <div className="space-y-4">
            {!isLoading && devices.length === 0 ? (
                <EmptyStateCard
                    icon={Monitor}
                    title="No devices available"
                    description="No devices are currently assigned to your roles."
                />
            ) : (
                <>
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-lg border">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                                <Monitor className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                                    Available Devices
                                </h3>
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                    {isLoading
                                        ? "Loading devices..."
                                        : `${totalDevicesCount} device${totalDevicesCount !== 1 ? "s" : ""} assigned to your roles`}
                                </p>
                            </div>
                        </div>
                        {isLoading && <Skeleton className="w-12 h-4 ml-auto" />}
                    </div>

                    {/* Device Cards Grid */}
                    <div className="grid gap-4 grid-cols-2 lg:grid-cols-3">
                        {isLoading ? (
                            renderSkeletonCards()
                        ) : (
                            devices.map((device) => (
                                <Card
                                    key={device.id}
                                    className="min-w-0 h-full flex flex-col hover:shadow-lg transition-all duration-200 cursor-pointer border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 bg-white dark:bg-slate-900"
                                    onClick={() => {
                                        if (!device.isManuallyAddedMachine)
                                            handleDeviceClick(device.id)
                                    }}
                                >
                                    <CardContent className="p-6 min-w-0 flex flex-col flex-1">
                                        {/* Device Header */}
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-center space-x-4 min-w-0">
                                                <div className="w-12 h-12 flex items-center justify-center shrink-0">
                                                    {getOSIcon(device.os)}
                                                </div>
                                                <div className="flex-1 min-w-0">

                                                    <h4
                                                        className="font-semibold text-lg text-slate-900 dark:text-slate-100 truncate"
                                                        data-testid={`text-device-name-${device.id}`}
                                                    >
                                                        {device.friendlyName ? device.friendlyName : device.name}
                                                    </h4>
                                                    {device.friendlyName && (
                                                        <div className="text-xs text-muted-foreground truncate">
                                                            Host Name: {device.name}
                                                        </div>
                                                    )}
                                                    <div className="flex items-start gap-2 mt-1 min-h-[2.5rem]">
                                                        <StatusIndicator lastSeen={device.lastSeen} showDot isManualDevice={device?.isManuallyAddedMachine || false} />
                                                        <span className="text-sm text-slate-600 dark:text-slate-400 leading-snug line-clamp-2">
                                                            {device.os}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Device Details */}
                                        <div className="space-y-3 mb-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Cpu className="w-4 h-4 text-slate-500" />
                                                    <span className="text-sm text-slate-600 dark:text-slate-400">
                                                        Agent
                                                    </span>
                                                </div>
                                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                    v{device.agentVersion}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Status Pills */}
                                        <div className="flex flex-col gap-2 mb-4 min-w-0 w-full">
                                            {/*
                                            <Badge
                                                variant="secondary"
                                                className="text-xs !block w-full max-w-full min-w-0 whitespace-normal break-all h-auto text-left leading-snug font-normal"
                                            >
                                                {device.profile}
                                            </Badge>
                                            */}

                                            <Badge
                                                variant={getOperatingModeColor(device.mode || "", device.profileMode || "")}
                                                className="text-xs capitalize self-start"
                                            >
                                                {labelStatus(device.mode || "", device.profileMode || "")}
                                            </Badge>
                                        </div>

                                        {/* Action Button */}
                                        <div
                                            className="mt-auto pt-3 border-t border-slate-100 dark:border-slate-700"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {isOwnDevices(device) ?
                                                <div className="flex justify-end flex-wrap gap-2">
                                                    <Badge
                                                        variant="default"
                                                        className="flex items-center gap-1 text-xs capitalize px-2 py-1"
                                                    >
                                                        <Check className="h-3 w-3" />
                                                        ASSIGNED
                                                    </Badge>
                                                </div> :
                                                shouldShowConnectButton(device) ?
                                                    <div className="space-y-2 flex flex-col items-end">
                                                        <Button
                                                            size="sm"
                                                            disabled={deviceLoadingState[device.id]?.loading}
                                                            onClick={() => handleConnect(device.id)}
                                                            data-testid={`button-connect-${device.id}`}
                                                            variant="default"
                                                            className="w-1/3 gap-2"
                                                        >
                                                            <ShieldCheck className="w-4 h-4" />Connect
                                                            {deviceLoadingState[device.id]?.loading && <Loader2 className="w-4 h-4 text-white-600 dark:text-white-400 animate-spin" />}

                                                        </Button>
                                                        {remainingTimes[device.id] && (
                                                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                                <Clock className="w-3 h-3" />
                                                                <span>{remainingTimes[device.id]}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    : <Button
                                                        size="sm"
                                                        onClick={() => handleConnect(device.id)}
                                                        data-testid={`button-connect-${device.id}`}
                                                        variant="outline"
                                                        className="w-full gap-2 border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:border-blue-300 dark:hover:border-blue-600 bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50"
                                                    >
                                                        <ShieldCheck className="w-4 h-4" />
                                                        Request Connect Access
                                                    </Button>}

                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border">
                            <div className="text-sm text-slate-600 dark:text-slate-400">
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage <= 1}
                                    className="gap-1"
                                    data-testid="button-prev-page"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Previous
                                </Button>
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        const page = i + 1;
                                        return (
                                            <Button
                                                key={page}
                                                variant={currentPage === page ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => handlePageChange(page)}
                                                className="w-8 h-8 p-0"
                                                data-testid={`button-page-${page}`}
                                            >
                                                {page}
                                            </Button>
                                        );
                                    })}
                                    {totalPages > 5 && (
                                        <>
                                            <span className="text-slate-500 px-2">...</span>
                                            <Button
                                                variant={currentPage === totalPages ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => handlePageChange(totalPages)}
                                                className="w-8 h-8 p-0"
                                                data-testid={`button-page-${totalPages}`}
                                            >
                                                {totalPages}
                                            </Button>
                                        </>
                                    )}
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage >= totalPages}
                                    className="gap-1"
                                    data-testid="button-next-page"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </>

            )}

            {/* Request Access Dialog */}
            <RequestConnectAccessDialog
                open={showAccessDialog}
                onOpenChange={setShowAccessDialog}
                onSubmitSuccess={handleRequestSubmitSuccess}
                deviceName={selectedDevice?.name || ""}
                applicationName="Device Access"
                osType={selectedDevice ? getOSType(selectedDevice) : "windows"}
                userList={usersList}
                sessionRequest={true}
                resourceType="GROUP"
                resourcseId={selectedDevice?.whiteswanAccessGroupId}
                isWhitewanAccess={"true"}
                isFromEndPoint={true}
                isFromEndPointApp={false}
                tenantName={tenantName}
                device={selectedDevice}
                publisherId={""}
                resourceName={"Whiteswan Access"}
                resourcePath={"Whiteswan Accesss"}
            />

        </div>
    );
}
